import { useSetAtom } from 'jotai';
import { useAtomCallback } from 'jotai/utils';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useParams } from 'react-router-dom';

import { MeasureSchemaType } from '@grow-therapy-team/seedling-components';

import { MeasureSchemaAndAnswers } from '../../components/measures/types';
import { useTrackEvent } from '../../segment/segment';
import { EventTypeEnum } from '../../segment/types';
import { SOFT_BLOCK_GET_STARTED_STEP, SoftBlockStep, softBlockCurrentStepAtom } from '../state';

/**
 * Low-level Segment tracking callbacks for the soft-block (pre-session measures)
 * client flow. Centralizes the `patientShortId` / `providerShortId` plumbing so
 * each surface just calls the relevant tracker. Prefer the higher-level hooks
 * below (`useSoftBlock*Tracking`) inside components so the components stay free
 * of effect/ref/atom analytics plumbing. The eligibility event is tracked
 * separately in `useEnterWaitingRoomCallback`, where the routing decision is made.
 */
export function useSoftBlockTracking() {
  const { patientShortId, providerShortId } = useParams();
  const { userViewed, userClicked, userInputted } = useTrackEvent();

  return useMemo(
    () => ({
      trackGetStartedViewed: () =>
        userViewed(EventTypeEnum.SOFT_BLOCK_GET_STARTED_VIEWED, { patientShortId, providerShortId }),
      trackGetStartedClicked: () =>
        userClicked(EventTypeEnum.SOFT_BLOCK_GET_STARTED_CLICKED, { patientShortId, providerShortId }),
      trackMeasureViewed: (measureType: string) =>
        userViewed(EventTypeEnum.SOFT_BLOCK_MEASURE_VIEWED, { patientShortId, providerShortId, measureType }),
      trackMeasureCompleted: (measureType: string) =>
        userInputted(EventTypeEnum.SOFT_BLOCK_MEASURE_COMPLETED, { patientShortId, providerShortId, measureType }),
      trackMeasureSkipped: (measureType: string) =>
        userClicked(EventTypeEnum.SOFT_BLOCK_MEASURE_SKIPPED, { patientShortId, providerShortId, measureType }),
      trackGuardrailPromptShown: (softBlockStep: SoftBlockStep) =>
        userViewed(EventTypeEnum.SOFT_BLOCK_GUARDRAIL_PROMPT_SHOWN, { patientShortId, providerShortId, softBlockStep }),
      trackAutoTransitioned: (softBlockStep: SoftBlockStep) =>
        userInputted(EventTypeEnum.SOFT_BLOCK_AUTO_TRANSITIONED, { patientShortId, providerShortId, softBlockStep }),
    }),
    [userViewed, userClicked, userInputted, patientShortId, providerShortId],
  );
}

/**
 * Fires the get-started page-view event exactly once on mount. Keeps the
 * fire-once ref/effect out of the `Page` component.
 */
export function useTrackSoftBlockGetStartedView() {
  const { trackGetStartedViewed } = useSoftBlockTracking();
  const hasTrackedRef = useRef(false);

  useEffect(
    function trackGetStartedPageViewOnce() {
      if (hasTrackedRef.current) return;
      hasTrackedRef.current = true;
      trackGetStartedViewed();
    },
    [trackGetStartedViewed],
  );
}

/**
 * Flow-level (page-spanning) tracking for the soft-block `PageWrapper`. Seeds the
 * shared current-step to the get-started page on mount and exposes step-aware
 * trackers for the get-started CTA, the guardrail prompt, and the
 * auto-transition. Reads the current step at call time so callers don't need to
 * thread it through. Keeps this plumbing out of the component.
 */
export function useSoftBlockFlowTracking() {
  const { trackGetStartedClicked, trackGuardrailPromptShown, trackAutoTransitioned } = useSoftBlockTracking();
  const setCurrentStep = useSetAtom(softBlockCurrentStepAtom);
  const getCurrentStep = useAtomCallback(useCallback((get) => get(softBlockCurrentStepAtom), []));

  useEffect(
    function seedSoftBlockStep() {
      // The flow always starts on the get-started page.
      setCurrentStep(SOFT_BLOCK_GET_STARTED_STEP);
    },
    [setCurrentStep],
  );

  return useMemo(
    () => ({
      trackGetStartedClicked,
      trackGuardrailPromptShown: () => trackGuardrailPromptShown(getCurrentStep()),
      trackAutoTransitioned: () => trackAutoTransitioned(getCurrentStep()),
    }),
    [trackGetStartedClicked, trackGuardrailPromptShown, trackAutoTransitioned, getCurrentStep],
  );
}

/**
 * Measure-level tracking for the soft-block `PreSessionMeasures` page. Owns the
 * "which measure is the client on" bookkeeping (the shared step atom + a ref for
 * the skip attribution), fires the initial measure view (which `onPageIndexChange`
 * does not cover), and exposes handlers the component wires directly into
 * `PendingMeasuresForm`. Keeps the refs/effects out of the component.
 */
export function useSoftBlockMeasuresTracking(pages: MeasureSchemaAndAnswers[] | undefined) {
  const { trackMeasureViewed, trackMeasureSkipped } = useSoftBlockTracking();
  const setCurrentStep = useSetAtom(softBlockCurrentStepAtom);
  const currentMeasureTypeRef = useRef<MeasureSchemaType | undefined>(undefined);
  const hasTrackedInitialViewRef = useRef(false);

  const goToMeasureStep = useCallback(
    (measureType: MeasureSchemaType | undefined) => {
      if (!measureType) return;
      currentMeasureTypeRef.current = measureType;
      setCurrentStep(measureType);
      trackMeasureViewed(measureType);
    },
    [setCurrentStep, trackMeasureViewed],
  );

  // `onPageIndexChange` does not fire for the initial page, so track the first
  // measure view here once the pages are available.
  useEffect(
    function trackInitialMeasureView() {
      if (hasTrackedInitialViewRef.current) return;
      const firstMeasureType = pages?.[0]?.schema.type;
      if (!firstMeasureType) return;
      hasTrackedInitialViewRef.current = true;
      goToMeasureStep(firstMeasureType);
    },
    [pages, goToMeasureStep],
  );

  const handlePageIndexChange = useCallback(
    (pageIndex: number) => {
      goToMeasureStep(pages?.[pageIndex]?.schema.type);
    },
    [pages, goToMeasureStep],
  );

  const trackCurrentMeasureSkipped = useCallback(() => {
    const measureType = currentMeasureTypeRef.current;
    if (measureType) {
      trackMeasureSkipped(measureType);
    }
  }, [trackMeasureSkipped]);

  return { handlePageIndexChange, trackCurrentMeasureSkipped };
}
